import React from "react";
import { toast } from "react-toastify";
import withPageFilters from "../../Layout/withPageFilters";
import Loader from "../../components/Loader/Loader";
import EmptyState from "../../components/EmptyState";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useGetConversionReportQuery } from "../../store/api/reportsApi";

const cardStyle = {
  backgroundColor: "var(--bg-elevated)",
  borderRadius: "8px",
  overflow: "hidden",
  position: "relative",
};

const formatPct = (value) => `${(value ?? 0).toFixed(2)}%`;

const DispositionBadges = ({ breakdown }) => {
  const entries = Object.entries(breakdown || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <span className="text-secondary">-</span>;
  return (
    <div className="d-flex flex-wrap gap-1">
      {entries.map(([disposition, count]) => (
        <span
          key={disposition}
          className="badge"
          style={{ backgroundColor: "var(--bg-elevated-2)", color: "var(--text-secondary)", fontWeight: 400 }}
          title={disposition}
        >
          {disposition}: {count}
        </span>
      ))}
    </div>
  );
};

// Shown over a card's content while a new filter selection is being fetched,
// so applying a filter gives clear feedback instead of the table just
// quietly changing (or appearing to do nothing) a moment later.
const CardLoadingOverlay = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 5,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      backgroundColor: "rgba(23, 35, 61, 0.85)",
      backdropFilter: "blur(1px)",
    }}
  >
    <div className="spinner-border" role="status" style={{ width: "2rem", height: "2rem", color: "var(--accent)" }}>
      <span className="visually-hidden">Loading...</span>
    </div>
    <span style={{ color: "var(--text-primary)", fontSize: "0.85rem" }}>Updating report...</span>
  </div>
);

const ConversionReport = () => {
  const queryParams = useQueryParams();

  const { data, error, isLoading, isFetching } = useGetConversionReportQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  React.useEffect(() => {
    if (error) toast.error("Failed to load conversion report");
  }, [error]);

  const summary = data?.summary || [];
  const trend = data?.trend || [];
  const showOverlay = isFetching && !isLoading;

  if (isLoading) return <Loader message="Building conversion report..." />;

  return (
    <div className="mt-2">
      <h5 className="text-white mb-3">Conversion & Disposition Report</h5>

      {/* Publisher summary for the selected date range */}
      <div className="card mb-4" style={cardStyle}>
        {showOverlay && <CardLoadingOverlay />}
        <div className="card-header text-white" style={{ backgroundColor: "var(--bg-surface)" }}>
          Publisher Summary
        </div>
        {summary.length === 0 ? (
          <EmptyState message="No calls found for the selected filters." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-dark table-hover mb-0" style={{ backgroundColor: "var(--bg-elevated)" }}>
              <thead>
                <tr>
                  <th>Publisher</th>
                  <th>Total Calls</th>
                  <th>Sales</th>
                  <th>Conversion Rate</th>
                  <th>Disposition Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => (
                  <tr key={row.publisher}>
                    <td>{row.publisher}</td>
                    <td>{row.totalCalls}</td>
                    <td>{row.sales}</td>
                    <td>
                      <span
                        className="badge"
                        style={{ backgroundColor: row.conversionRate >= 10 ? "var(--success)" : "var(--bg-elevated-2)" }}
                      >
                        {formatPct(row.conversionRate)}
                      </span>
                    </td>
                    <td>
                      <DispositionBadges breakdown={row.dispositionBreakdown} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Daily trend for the selected date range */}
      <div className="card" style={cardStyle}>
        {showOverlay && <CardLoadingOverlay />}
        <div className="card-header text-white" style={{ backgroundColor: "var(--bg-surface)" }}>
          Daily Trend
        </div>
        {trend.length === 0 ? (
          <EmptyState message="No calls found for the selected filters." />
        ) : (
          <div style={{ overflowX: "auto", maxHeight: "500px" }}>
            <table className="table table-dark table-hover mb-0" style={{ backgroundColor: "var(--bg-elevated)" }}>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "var(--bg-surface)" }}>
                <tr>
                  <th>Date</th>
                  <th>Publisher</th>
                  <th>Total Calls</th>
                  <th>Sales</th>
                  <th>Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                {trend.map((row) => (
                  <tr key={`${row.period}-${row.publisher}`}>
                    <td>{row.period}</td>
                    <td>{row.publisher}</td>
                    <td>{row.totalCalls}</td>
                    <td>{row.sales}</td>
                    <td>{formatPct(row.conversionRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default withPageFilters(ConversionReport);
